import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { IColumn } from "@fluentui/react";
import { MessageBar, MessageBarType } from "@fluentui/react";
import PageHeader from "@/ui-controls/PageHeader";
import useTelemetrySearch, {
  DEFAULT_PAGE_SIZE,
  type HeaderTypeValue,
} from "@/components/telemetry/search/useTelemetrySearch";
import getTelemetryColumns from "@/components/telemetry/search/telemetryColumns";
import {
  TelemetrySortField,
  type TelemetrySortFieldType,
} from "@/components/telemetry/search/telemetrySortField";
import PaginationFooter, {
  type PageSize,
} from "@/components/telemetry/common/PaginationFooter";
import TelemetrySearchToolbar from "@/components/telemetry/search/TelemetrySearchToolbar";
import TelemetryTable from "@/components/telemetry/search/TelemetryTable";
import type { TelemetrySummary } from "@/api/telemetry";
import styles from "@/components/telemetry/search/TelemetrySearch.module.css";

function TelemetrySearchPage() {
  const navigate = useNavigate();

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [customerId, setCustomerId] = useState("");
  const [method, setMethod] = useState("");
  const [path, setPath] = useState("");
  const [statusCode, setStatusCode] = useState("");
  const [apiName, setApiName] = useState("");
  const [fromTimestamp, setFromTimestamp] = useState("");
  const [toTimestamp, setToTimestamp] = useState("");
  const [headerName, setHeaderName] = useState("");
  const [headerValue, setHeaderValue] = useState("");
  const [headerType, setHeaderType] = useState<HeaderTypeValue>("request");
  const [sortField, setSortField] = useState<TelemetrySortFieldType>(
    TelemetrySortField.Timestamp,
  );
  const [isSortDescending, setIsSortDescending] = useState(true);

  const { items, totalCount, isLoading, error, refetch } =
    useTelemetrySearch({
      pageNumber,
      pageSize,
      customerId,
      method,
      path,
      statusCode,
      apiName,
      fromTimestamp,
      toTimestamp,
      headerName,
      headerValue,
      headerType,
      sortField,
      isSortDescending,
    });

  const resetPage = useCallback(() => setPageNumber(1), []);

  const handleCustomerIdChange = useCallback(
    (value: string) => {
      setCustomerId(value);
      resetPage();
    },
    [resetPage],
  );
  const handleMethodChange = useCallback(
    (value: string) => {
      setMethod(value);
      resetPage();
    },
    [resetPage],
  );
  const handlePathChange = useCallback(
    (value: string) => {
      setPath(value);
      resetPage();
    },
    [resetPage],
  );
  const handleStatusCodeChange = useCallback(
    (value: string) => {
      setStatusCode(value);
      resetPage();
    },
    [resetPage],
  );
  const handleApiNameChange = useCallback(
    (value: string) => {
      setApiName(value);
      resetPage();
    },
    [resetPage],
  );
  const handleFromTimestampChange = useCallback(
    (value: string) => {
      setFromTimestamp(value);
      resetPage();
    },
    [resetPage],
  );
  const handleToTimestampChange = useCallback(
    (value: string) => {
      setToTimestamp(value);
      resetPage();
    },
    [resetPage],
  );
  const handleHeaderNameChange = useCallback(
    (value: string) => {
      setHeaderName(value);
      resetPage();
    },
    [resetPage],
  );
  const handleHeaderValueChange = useCallback(
    (value: string) => {
      setHeaderValue(value);
      resetPage();
    },
    [resetPage],
  );
  const handleHeaderTypeChange = useCallback(
    (value: HeaderTypeValue) => {
      setHeaderType(value);
      resetPage();
    },
    [resetPage],
  );

  const handlePageSizeChange = useCallback((size: PageSize) => {
    setPageSize(size);
    setPageNumber(1);
  }, []);

  const handleColumnClick = useCallback(
    (column: IColumn) => {
      const field = column.data as TelemetrySortFieldType | undefined;
      if (!field) return;

      if (field === sortField) {
        setIsSortDescending((prev) => !prev);
      } else {
        setSortField(field);
        setIsSortDescending(true);
      }
    },
    [sortField],
  );

  const handleRowClick = useCallback(
    (item: TelemetrySummary) => {
      navigate(`/telemetry/detail/${item.id}`);
    },
    [navigate],
  );

  const columns = useMemo(() => {
    const cols = getTelemetryColumns();
    return cols.map((col) => ({
      ...col,
      isSorted: col.data === sortField,
      isSortedDescending: col.data === sortField ? isSortDescending : undefined,
    }));
  }, [sortField, isSortDescending]);

  return (
    <div className={styles.page}>
      <PageHeader
        title="API Usage"
        subtitle="Search and inspect API request telemetry across all customers and endpoints."
      />
      <TelemetrySearchToolbar
        customerId={customerId}
        method={method}
        path={path}
        statusCode={statusCode}
        apiName={apiName}
        fromTimestamp={fromTimestamp}
        toTimestamp={toTimestamp}
        headerName={headerName}
        headerValue={headerValue}
        headerType={headerType}
        onCustomerIdChange={handleCustomerIdChange}
        onMethodChange={handleMethodChange}
        onPathChange={handlePathChange}
        onStatusCodeChange={handleStatusCodeChange}
        onApiNameChange={handleApiNameChange}
        onFromTimestampChange={handleFromTimestampChange}
        onToTimestampChange={handleToTimestampChange}
        onHeaderNameChange={handleHeaderNameChange}
        onHeaderValueChange={handleHeaderValueChange}
        onHeaderTypeChange={handleHeaderTypeChange}
        onRefresh={refetch}
      />
      {error && (
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      )}
      <TelemetryTable
        items={items}
        columns={columns}
        isLoading={isLoading}
        onColumnClick={handleColumnClick}
        onRowClick={handleRowClick}
      />
      <PaginationFooter
        pageNumber={pageNumber}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPageNumber}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}

export default TelemetrySearchPage;
